<script lang="ts" setup>
import { ArrowUpIcon, ArrowsPointingInIcon, ArrowsPointingOutIcon, ArrowsRightLeftIcon, ChatBubbleLeftRightIcon, ClockIcon, PlusIcon, StopIcon, XMarkIcon } from '@heroicons/vue/24/solid';
import { useLocalStorage } from '@vueuse/core';
import { nextTick, ref, useTemplateRef, watch } from 'vue';
import { useChatBot } from '../../composables/useChatBot';
import ChatHistory from './ChatHistory.vue';
import ChatMessage from './ChatMessage.vue';

const {
    messages,
    conversationId,
    conversations,
    suggestions,
    showHistory,
    isLoadingHistory,
    isOpen,
    isStreaming,
    isFetching,
    error,
    messagesContainer,
    send,
    cancel,
    toggle,
    newConversation,
    toggleHistory,
    switchConversation,
    deleteConversation,
} = useChatBot();

const input = ref('');
const isExpanded = useLocalStorage('chatbot-expanded', false);
const side = useLocalStorage<'left' | 'right'>('chatbot-side', 'right');
const width = useLocalStorage('chatbot-width', 384);
const textareaRef = useTemplateRef<HTMLTextAreaElement>('textarea');

const MIN_WIDTH = 384;
const MAX_WIDTH = 900;
const isResizing = ref(false);

function toggleSide() {
    side.value = side.value === 'right' ? 'left' : 'right';
}

function startResize(event: PointerEvent) {
    event.preventDefault();
    isResizing.value = true;
    const startX = event.clientX;
    const startWidth = width.value;

    function onMove(e: PointerEvent) {
        const delta = side.value === 'right' ? startX - e.clientX : e.clientX - startX;
        width.value = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startWidth + delta));
    }

    function onUp() {
        isResizing.value = false;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
    }

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
}

function onSend() {
    const message = input.value.trim();

    if(!message || isStreaming.value) {
        return;
    }

    send(message);
    input.value = '';
    resetHeight();
}

function onSuggestion(text: string) {
    send(text);
}

function onKeydown(event: KeyboardEvent) {
    if(event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        onSend();
    }
}

function autoGrow() {
    const el = textareaRef.value;

    if(!el) {
        return;
    }

    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
}

function resetHeight() {
    nextTick(() => {
        const el = textareaRef.value;

        if(el) {
            el.style.height = 'auto';
        }
    });
}

function focusInput() {
    nextTick(() => textareaRef.value?.focus());
}

watch(isOpen, (open) => {
    if(open) {
        focusInput();
    }
});
</script>

<template>
    <Teleport to="body">
        <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="translate-y-4 opacity-0"
            enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="translate-y-0 opacity-100"
            leave-to-class="translate-y-4 opacity-0">
            <div
                v-if="isOpen"
                class="fixed bottom-20 z-40 flex flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl dark:border-neutral-700 dark:bg-neutral-900"
                :class="[
                    isExpanded ? 'h-[calc(100vh-6rem)]' : 'h-128',
                    side === 'left' ? 'left-6' : 'right-6',
                    isResizing ? 'select-none' : '',
                ]"
                :style="{ width: `${width}px` }">
                <div
                    class="group absolute inset-y-0 z-10 w-1.5 cursor-col-resize hover:bg-primary/40"
                    :class="side === 'left' ? 'right-0' : 'left-0'"
                    :aria-label="side === 'left' ? 'Resize chat from right edge' : 'Resize chat from left edge'"
                    role="separator"
                    aria-orientation="vertical"
                    @pointerdown="startResize" />
                <div class="flex items-center justify-between bg-primary px-4 py-3 text-white">
                    <div class="flex items-center gap-2">
                        <ChatBubbleLeftRightIcon class="size-5" />
                        <span class="font-heading text-sm font-bold">Alchemy Assistant</span>
                    </div>
                    <div class="flex items-center gap-1">
                        <button
                            type="button"
                            class="rounded p-1 hover:bg-white/20"
                            :aria-label="side === 'right' ? 'Move to left side' : 'Move to right side'"
                            @click="toggleSide">
                            <ArrowsRightLeftIcon class="size-4" />
                        </button>
                        <button
                            type="button"
                            class="rounded p-1 hover:bg-white/20"
                            :class="isExpanded ? 'bg-white/20' : ''"
                            aria-label="Toggle size"
                            @click="isExpanded = !isExpanded">
                            <ArrowsPointingInIcon
                                v-if="isExpanded"
                                class="size-4" />
                            <ArrowsPointingOutIcon
                                v-else
                                class="size-4" />
                        </button>
                        <button
                            type="button"
                            class="rounded p-1 hover:bg-white/20"
                            :class="showHistory ? 'bg-white/20' : ''"
                            aria-label="Conversation history"
                            @click="toggleHistory">
                            <ClockIcon class="size-4" />
                        </button>
                        <button
                            type="button"
                            class="rounded p-1 hover:bg-white/20"
                            aria-label="New conversation"
                            @click="newConversation">
                            <PlusIcon class="size-4" />
                        </button>
                        <button
                            type="button"
                            class="rounded p-1 hover:bg-white/20"
                            aria-label="Close"
                            @click="toggle">
                            <XMarkIcon class="size-4" />
                        </button>
                    </div>
                </div>

                <ChatHistory
                    v-if="showHistory"
                    :conversations="conversations"
                    :active-id="conversationId"
                    :is-loading="isLoadingHistory"
                    @select="switchConversation"
                    @delete="deleteConversation"
                    @back="toggleHistory" />

                <template v-else>
                    <div
                        :ref="(el) => { messagesContainer = el as HTMLElement; }"
                        class="flex flex-1 flex-col gap-3 overflow-y-auto p-4">
                        <div
                            v-if="messages.length === 0"
                            class="flex flex-1 flex-col items-center justify-center gap-4 px-4">
                            <ChatBubbleLeftRightIcon class="size-10 text-neutral-300 dark:text-neutral-600" />
                            <div class="text-center">
                                <p class="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                                    Hi! I'm your Alchemy Assistant.
                                </p>
                                <p class="mt-1 text-xs text-neutral-400 dark:text-neutral-500">
                                    I can help you manage agencies, lists, sends, templates, and subscribers.
                                </p>
                            </div>
                            <div class="flex flex-wrap justify-center gap-2">
                                <button
                                    v-for="suggestion in suggestions"
                                    :key="suggestion"
                                    type="button"
                                    class="rounded-full border border-neutral-200 px-3 py-1 text-xs text-neutral-600 transition-colors hover:border-primary hover:text-primary dark:border-neutral-700 dark:text-neutral-400 dark:hover:border-primary dark:hover:text-primary"
                                    @click="onSuggestion(suggestion)">
                                    {{ suggestion }}
                                </button>
                            </div>
                        </div>
                        <ChatMessage
                            v-for="msg in messages"
                            :key="msg.id"
                            :message="msg"
                            :is-last="msg === messages[messages.length - 1]"
                            :is-streaming="isStreaming"
                            :is-fetching="isFetching" />
                    </div>

                    <div
                        v-if="error"
                        class="border-t border-red-200 bg-red-50 px-4 py-2 text-xs text-red-600 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
                        {{ error }}
                    </div>

                    <div class="p-3">
                        <div class="flex flex-col rounded-2xl border border-neutral-200 bg-neutral-50 focus-within:border-neutral-400 dark:border-neutral-700 dark:bg-neutral-800 dark:focus-within:border-neutral-500">
                            <textarea
                                ref="textarea"
                                v-model="input"
                                rows="1"
                                class="max-h-30 resize-none border-none bg-transparent px-3.5 pt-3 pb-1 text-sm text-neutral-900 outline-none placeholder:text-neutral-400 dark:text-neutral-100 dark:placeholder:text-neutral-500"
                                placeholder="Ask about Alchemy..."
                                :disabled="isStreaming"
                                @input="autoGrow"
                                @keydown="onKeydown" />
                            <div class="flex items-center justify-end px-2 pb-2">
                                <button
                                    v-if="isStreaming"
                                    type="button"
                                    class="flex size-7 items-center justify-center rounded-lg bg-primary text-white transition-colors hover:bg-primary/90"
                                    aria-label="Stop response"
                                    @click="cancel">
                                    <StopIcon class="size-4" />
                                </button>
                                <button
                                    v-else
                                    type="button"
                                    class="flex size-7 items-center justify-center rounded-lg transition-colors"
                                    :class="input.trim()
                                        ? 'bg-primary text-white hover:bg-primary/90'
                                        : 'bg-neutral-200 text-neutral-400 dark:bg-neutral-700 dark:text-neutral-500'"
                                    :disabled="!input.trim()"
                                    aria-label="Send message"
                                    @click="onSend">
                                    <ArrowUpIcon class="size-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </Transition>

        <button
            type="button"
            class="fixed bottom-6 z-40 flex size-12 items-center justify-center rounded-full bg-primary text-white shadow-lg transition-transform hover:scale-105 active:scale-95"
            :class="side === 'left' ? 'left-6' : 'right-6'"
            @click="toggle">
            <ChatBubbleLeftRightIcon
                v-if="!isOpen"
                class="size-6" />
            <XMarkIcon
                v-else
                class="size-6" />
        </button>
    </Teleport>
</template>
